import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Box,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip as ChartTooltip, 
  Legend, 
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

// Complexity configuration (aligned with PolicyReviewPage)
const complexityConfig = {
  simple: {
    label: 'Simple',
    weight: 1.0,
    description: 'No accidents in 6 years, no tickets, local driving'
  },
  moderate: {
    label: 'Moderate',
    weight: 1.3,
    description: 'Has accidents/tickets, standard docs'
  },
  complex: {
    label: 'Complex',
    weight: 1.8,
    description: 'Multiple issues, LOE required, complex docs'
  }
};

// Generate mock data with complexity distribution
const generateUnderwriterData = () => {
  const underwriters = ['John Smith', 'Emma Wilson', 'Michael Brown', 'Sarah Lee', 'David Chen'];
  
  return underwriters.map(name => {
    const simple = Math.floor(Math.random() * 20 + 5);
    const moderate = Math.floor(Math.random() * 15 + 3);
    const complex = Math.floor(Math.random() * 10 + 2);
    const totalPolicies = simple + moderate + complex;
    
    // Calculate weighted efficiency score
    const totalWeight = (simple * 1) + (moderate * 1.3) + (complex * 1.8);
    const avgProcessingHours = (Math.random() * 6 + 4).toFixed(1);
    const efficiencyScore = Math.min(100, Math.round(
      (totalWeight * 100) / totalPolicies - (avgProcessingHours * 0.8)
    ));

    return {
      name,
      avatar: name[0],
      totalPolicies,
      annualCases: totalPolicies * 12,
      efficiencyScore,
      avgProcessingHours,
      complexityDistribution: { simple, moderate, complex },
      qualityScore: Math.floor(Math.random() * 31) + 70 // 生成70-100的随机数
    };
  });
};

const EfficiencyProgress = ({ value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <LinearProgress 
      variant="determinate" 
      value={value} 
      sx={{ 
        width: '100px',
        height: '8px',
        borderRadius: '4px',
        mr: 1,
        '& .MuiLinearProgress-bar': {
          borderRadius: '4px',
          backgroundColor: value > 80 ? '#4caf50' : 
                         value > 60 ? '#ffb74d' : '#f44336'
        }
      }}
    />
    <Typography variant="body2">{value}%</Typography>
  </Box>
);

const ComplexityChip = ({ level }) => {
  const config = complexityConfig[level];
  return (
    <Tooltip title={config.description}>
      <Chip 
        label={config.label}
        size="small"
        variant="outlined"
        sx={{ 
          borderColor: level === 'simple' ? '#4caf50' :
                     level === 'moderate' ? '#ffb74d' : '#f44336',
          color: level === 'simple' ? '#4caf50' :
                level === 'moderate' ? '#ffb74d' : '#f44336'
        }}
      />
    </Tooltip>
  );
};

// 生成分行数据
const generateBranchData = () => {
  const branches = [
    {
      id: 'all',
      name: 'All Branches',
      location: 'Company Wide'
    },
    {
      id: 'downtown',
      name: 'Downtown Branch',
      location: 'Vancouver Downtown'
    },
    {
      id: 'richmond',
      name: 'Richmond Branch',
      location: 'Richmond Center'
    },
    {
      id: 'burnaby',
      name: 'Burnaby Branch',
      location: 'Metrotown'
    }
  ];

  return branches.map(branch => {
    if (branch.id === 'all') {
      return {
        ...branch,
        metrics: {
          monthlyPolicies: 450,
          annualPolicies: 450 * 12,
          avgProcessingTime: 5.2,
          totalBrokers: 15,
          avgQualityScore: 85
        }
      };
    }

    // 为每个分行生成独立数据
    let monthlyPolicies, totalBrokers;
    switch(branch.id) {
      case 'downtown':
        monthlyPolicies = 180;
        totalBrokers = 6;
        break;
      case 'richmond':
        monthlyPolicies = 120;
        totalBrokers = 4;
        break;
      case 'burnaby':
        monthlyPolicies = 150;
        totalBrokers = 4;
        break;
      default:
        monthlyPolicies = 100;
        totalBrokers = 3;
    }

    return {
      ...branch,
      metrics: {
        monthlyPolicies,
        annualPolicies: monthlyPolicies * 12,
        avgProcessingTime: parseFloat((Math.random() * 2 + 4).toFixed(1)), // 生成4.0-6.0之间的随机数
        totalBrokers,
        avgQualityScore: Math.floor(Math.random() * 10 + 80) // 生成80-89之间的随机数
      }
    };
  });
};

const UwstatisticsPage = () => {
  const [selectedBranch, setSelectedBranch] = useState('all');
  const branchesData = generateBranchData();
  const currentBranch = branchesData.find(b => b.id === selectedBranch);
  const underwriters = generateUnderwriterData();
  const [expandGuide, setExpandGuide] = useState(false);
  const [expandQualityGuide, setExpandQualityGuide] = useState(false);

  const COLORS = ['#1976d2', '#4caf50', '#ff9800', '#e91e63', '#9c27b0'];

  // 生成月度数据
  const generateMonthlyData = () => {
    return Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2024, i).toLocaleString('en-US', { month: 'short' }),
      approvals: 80 + Math.floor(Math.random() * 40) // 模拟审核量
    }));
  };

  // 生成核保人分布数据
  const generateUWDistributionData = () => {
    const total = underwriters.slice(0,5).reduce((sum, uw) => sum + uw.totalPolicies, 0);
    return underwriters.slice(0,5).map(uw => ({
      name: uw.name,
      value: Math.round((uw.totalPolicies / total) * 100)
    }));
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* 分行选择标签 - 移动到最顶部 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedBranch}
            onChange={(_, newValue) => setSelectedBranch(newValue)}
            variant="scrollable"
          >
            {branchesData.map((branch) => (
              <Tab
                key={branch.id}
                label={branch.name}
                value={branch.id}
                sx={{ 
                  minWidth: 120,
                  fontSize: '0.875rem',
                  fontWeight: 500 
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Typography variant="h4" sx={{ 
          color: '#1976d2', 
          mb: 2,
          fontWeight: 600,
          mt: 2  // 添加顶部间距
        }}>
          Underwriting Performance Dashboard
        </Typography>

        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
       
        </Typography>

        {/* Key Metrics Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <AssessmentIcon sx={{ color: '#1976d2', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Annual Submitted
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.annualPolicies}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <AssessmentIcon sx={{ color: '#4caf50', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Monthly Submitted
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.monthlyPolicies}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <TrendingUpIcon sx={{ color: '#4caf50', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Avg Time
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.avgProcessingTime}h</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <GroupIcon sx={{ color: '#ff9800', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Underwriters
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.totalBrokers}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <MoneyIcon sx={{ color: '#f44336', fontSize: '1.2rem', mr: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary">
                    Quality
                  </Typography>
                </Box>
                <Typography variant="h5">{currentBranch.metrics.avgQualityScore}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 可折叠的复杂度指南 */}
        <Paper elevation={0} sx={{ 
          p: 2, 
          mb: 4, 
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f8f9fa'
          }
        }}>
          <Box 
            onClick={() => setExpandGuide(!expandGuide)}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              <ExpandMoreIcon sx={{ 
                transform: expandGuide ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s'
              }}/>
            </IconButton>
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              Case Complexity Classification
            </Typography>
          </Box>
          
          <Collapse in={expandGuide}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {/* Simple Cases */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: '4px solid #4caf50',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Simple" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">Low Risk Profile</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • No claims in 6+ years
                    <br />
                    • Clean driving record
                    <br />
                    • Local jurisdiction
                  </Typography>
                </Box>
              </Grid>

              {/* Moderate Cases */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: '4px solid #ffb74d',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Moderate" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">Standard Risk</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • Minor infractions
                    <br />
                    • Single claim history
                    <br />
                    • Standard documents
                  </Typography>
                </Box>
              </Grid>

              {/* Complex Cases */}
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2,
                  borderLeft: '4px solid #f44336',
                  bgcolor: '#fff',
                  borderRadius: 1,
                  boxShadow: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      label="Complex" 
                      size="small" 
                      color="error" 
                      variant="outlined"
                      sx={{ mr: 1.5 }}
                    />
                    <Typography variant="subtitle2">High Complexity</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    • Multiple claims
                    <br />
                    • Cross-border exposure
                    <br />
                    • LOE required
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Quality Score Guide */}
        <Paper elevation={0} sx={{ 
          p: 2, 
          mb: 4, 
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f8f9fa'
          }
        }}>
          <Box 
            onClick={() => setExpandQualityGuide(!expandQualityGuide)}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              <ExpandMoreIcon sx={{ 
                transform: expandQualityGuide ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s'
              }}/>
            </IconButton>
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              Quality Score Algorithm
            </Typography>
          </Box>
          
          <Collapse in={expandQualityGuide}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 1, mt: 2 }}>
              <Typography variant="body1" paragraph>
                The Quality Score is calculated using a weighted algorithm that considers multiple factors:
              </Typography>

              <Grid container spacing={3}>
                {/* Case Complexity */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #1976d2',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Case Complexity Weighting
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>
                        <Chip label="Simple" size="small" color="success" variant="outlined" sx={{ mr: 1 }} />
                        Base weight: 1.0
                      </li>
                      <li>
                        <Chip label="Moderate" size="small" color="warning" variant="outlined" sx={{ mr: 1 }} />
                        Base weight: 1.3
                      </li>
                      <li>
                        <Chip label="Complex" size="small" color="error" variant="outlined" sx={{ mr: 1 }} />
                        Base weight: 1.8
                      </li>
                    </Box>
                  </Box>
                </Grid>

                {/* Time Factor */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #4caf50',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Time Efficiency Factor
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Processing time ≤4h: +10%</li>
                      <li>4h {'<'} time ≤6h: +5%</li>
                      <li>6h {'<'} time ≤8h: Base score</li>
                      <li>Time {'>'}8h: -5% per hour</li>
                    </Box>
                  </Box>
                </Grid>

                {/* Volume Adjustment */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #ff9800',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Volume Adjustment
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Base score per case: 100</li>
                      <li>+2% per case over 20/month</li>
                      <li>-1% per case under 10/month</li>
                    </Box>
                  </Box>
                </Grid>

                {/* Quality Deductions */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 2,
                    borderLeft: '4px solid #f44336',
                    bgcolor: '#f8f9fa',
                    borderRadius: 1
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      Quality Deductions
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                      <li>Documentation errors: -5% per case</li>
                      <li>Risk assessment errors: -10% per case</li>
                      <li>Resubmissions: -3% per occurrence</li>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="body1" sx={{ mt: 3, fontStyle: 'italic' }}>
                Final Score = (Complexity Weight × Time Factor) + Volume Adjustment - Quality Deductions
              </Typography>
            </Box>
          </Collapse>
        </Paper>

        {/* 新增图表容器 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* 月度审核柱状图 */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Monthly Policy Approvals
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={generateMonthlyData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#666' }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar 
                    dataKey="approvals" 
                    name="Approved Policies"
                    fill="#1976d2"
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* 核保人占比饼图 */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Underwriter Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={generateUWDistributionData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {generateUWDistributionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend 
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Underwriter Performance Table */}
        <Paper elevation={0} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ 
            mb: 3,
            color: '#424242',
            fontWeight: 600
          }}>
            Underwriter Performance
          </Typography>
          <TableContainer>
            <Table sx={{
              '& .MuiTableCell-root': {
                color: '#212121',
                borderBottom: 'none'
              }
            }}>
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }
                }}>
                  <TableCell>Underwriter</TableCell>
                  <TableCell align="center">Annual Policies</TableCell>
                  <TableCell align="center">Monthly Policies</TableCell>
                  <TableCell align="center">Simple</TableCell>
                  <TableCell align="center">Moderate</TableCell>
                  <TableCell align="center">Complex</TableCell>
                  <TableCell align="center">Avg Time</TableCell>
                  <TableCell align="center">Quality</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {underwriters.map((uw) => (
                  <TableRow 
                    key={uw.name} 
                    hover
                    sx={{
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ 
                          mr: 2,
                          bgcolor: '#e3f2fd', 
                          color: '#1976d2',
                          width: 32,
                          height: 32
                        }}>
                          {uw.avatar}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {uw.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {Math.floor(uw.totalPolicies * 12)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {uw.totalPolicies}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {uw.complexityDistribution.simple}
                    </TableCell>
                    <TableCell align="center">
                      {uw.complexityDistribution.moderate}
                    </TableCell>
                    <TableCell align="center">
                      {uw.complexityDistribution.complex}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {uw.avgProcessingHours}h
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 
                            uw.qualityScore >= 90 ? '#4caf50' :
                            uw.qualityScore >= 80 ? '#ffb74d' : '#f44336'
                        }}
                      >
                        {uw.qualityScore}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default UwstatisticsPage; 